import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Text, Textarea, Button } from '@chakra-ui/react'
import { FaPencilAlt, FaPlus, FaStickyNote } from 'react-icons/fa'
import { colors } from '../../../../utilities/colors'
import { locationTypeLabels } from '../../../../constants/carpoolLocation'

export default function OrderLocationCard({ label, values, quoteLocation, locationType, accentColor, onClick, onChange, notesLabel }) {
  const { t } = useTranslation()
  const resolvedNotesLabel = notesLabel || t('Delivery Instructions')
  const cityStateZip = [quoteLocation?.city, quoteLocation?.state, quoteLocation?.postal_code].filter(Boolean).join(', ')
  const fullAddress = [values.address, values.address_2].filter(Boolean).join(', ')
  const resolvedType = locationType || values.type
  const typeLabel = resolvedType ? t(locationTypeLabels[resolvedType] || resolvedType) : ''

  const detailRow = (rowLabel, value) => value ? (
    <Box display="flex" justifyContent="space-between" gap={2}>
      <Text fontSize="xs" color={colors.grey300} flexShrink={0}>{rowLabel}</Text>
      <Text fontSize="xs" color={colors.grey100} fontWeight="medium" textAlign="right">{value}</Text>
    </Box>
  ) : null

  const isEditable = typeof onClick === 'function'
  const supportsInlineNotes = typeof onChange === 'function'
  const noteValue = values.external_notes || ''

  return (
    <Box
      w="full"
      minW={0}
      p={{ base: 3, md: 4 }}
      borderRadius="md"
      border="1px solid"
      borderColor={colors.deepOcean}
      bg={colors.grey900}
      borderLeft="4px solid"
      borderLeftColor={accentColor}
      textAlign="left"
      transition="all 0.2s"
      display="flex"
      flexDirection="column"
      gap={2}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
        <Text fontSize="md" fontWeight="semibold" color={accentColor} minW={0} flex={1}>
          {label}
          {cityStateZip && (
            <Text as="span" fontSize="sm" fontWeight="normal" color={colors.grey300} ml={2}>
              {cityStateZip}
            </Text>
          )}
        </Text>
        {isEditable && (
          <Button
            size="xs"
            variant="ghost"
            color={colors.grey300}
            _hover={{ color: accentColor, bg: 'transparent' }}
            onClick={onClick}
          >
            <FaPencilAlt size={10} style={{ marginRight: 4 }} />
            {t('Edit')}
          </Button>
        )}
      </Box>

      <Box display="flex" flexDirection="column" gap={1}>
        {detailRow(t('Location Name'), values.name)}
        {detailRow(t('Location Type'), typeLabel)}
        {detailRow(t('Address'), fullAddress)}
        {detailRow(t('Contact Name'), values.contact_name || values.name)}
        {detailRow(t('Phone'), values.contact_phone)}
        {detailRow(t('Email'), values.contact_email)}
      </Box>

      <InlineNoteSection
        value={noteValue}
        accentColor={accentColor}
        editable={supportsInlineNotes}
        label={resolvedNotesLabel}
        onSave={supportsInlineNotes ? (next) => onChange({ ...values, external_notes: next }) : null}
      />
    </Box>
  )
}

function InlineNoteSection({ value, accentColor, editable, onSave, label }) {
  const { t } = useTranslation()
  const [isEditing, setIsEditing] = useState(false)
  const [draft, setDraft] = useState(value)
  const textareaRef = useRef(null)

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus()
      const length = textareaRef.current.value.length
      textareaRef.current.setSelectionRange(length, length)
    }
  }, [isEditing])

  if (!editable && !value) return null

  if (!editable) {
    return (
      <Box mt={1} pt={2} borderTop="1px dashed" borderColor={colors.grey700} display="flex" flexDirection="column" gap={1}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <FaStickyNote size={10} color={colors.grey300} />
          <Text fontSize="xs" color={colors.grey300} fontWeight="semibold">{label}</Text>
        </Box>
        <Text fontSize="xs" color={colors.grey100} whiteSpace="pre-wrap">{value}</Text>
      </Box>
    )
  }

  const startEdit = () => {
    setDraft(value)
    setIsEditing(true)
  }

  const cancel = () => {
    setDraft(value)
    setIsEditing(false)
  }

  const save = () => {
    onSave(draft.trim())
    setIsEditing(false)
  }

  if (isEditing) {
    return (
      <Box mt={1} pt={2} borderTop="1px dashed" borderColor={colors.grey700} display="flex" flexDirection="column" gap={2}>
        <Text fontSize="xs" color={colors.grey300}>
          {label}{' '}
          <Text as="span" color={colors.grey300}>({t('PIN, gate codes, parking, hours, etc.')})</Text>
        </Text>
        <Textarea
          ref={textareaRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') cancel()
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) save()
          }}
          placeholder={t('e.g. Gate code 4821, park in back lot, ask for Mike')}
          rows={3}
          maxLength={255}
          bgColor={colors.grey900}
          borderColor={colors.grey700}
          color={colors.grey100}
          _focus={{ borderColor: colors.grey100, ringColor: colors.grey100 }}
        />
        <Text fontSize="xs" color={colors.grey300} alignSelf="flex-end">
          {draft.length}/255
        </Text>
        <Box display="flex" justifyContent="flex-end" gap={2}>
          <Button size="xs" variant="ghost" color={colors.grey300} px={3} onClick={cancel}>
            {t('Cancel')}
          </Button>
          <Button size="xs" bg={accentColor} color={colors.grey100} px={4} _hover={{ opacity: 0.85 }} onClick={save}>
            {t('Save')}
          </Button>
        </Box>
      </Box>
    )
  }

  if (value) {
    return (
      <Box
        mt={1}
        pt={2}
        borderTop="1px dashed"
        borderColor={colors.grey700}
        display="flex"
        flexDirection="column"
        gap={1}
        onClick={startEdit}
        cursor="pointer"
        role="button"
        _hover={{ '& .note-edit-icon': { color: accentColor } }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <FaStickyNote size={10} color={colors.grey300} />
            <Text fontSize="xs" color={colors.grey300} fontWeight="semibold">{label}</Text>
          </Box>
          <Box className="note-edit-icon" color={colors.grey300} display="flex" alignItems="center" gap={1}>
            <FaPencilAlt size={9} />
            <Text fontSize="xs">{t('Edit')}</Text>
          </Box>
        </Box>
        <Text fontSize="xs" color={colors.grey100} whiteSpace="pre-wrap">{value}</Text>
      </Box>
    )
  }

  return (
    <Box mt={2} pt={2} borderTop="1px dashed" borderColor={colors.grey700}>
      <Button
        w="full"
        size="sm"
        variant="outline"
        borderColor={accentColor}
        color={accentColor}
        borderStyle="dashed"
        _hover={{ bg: accentColor, color: colors.grey100, borderStyle: 'solid' }}
        onClick={startEdit}
        justifyContent="center"
      >
        <FaPlus size={11} style={{ marginRight: 8 }} />
        <Box as="span">
          {t('Add')} {label}
          <Text as="span" fontSize="xs" fontWeight="normal" ml={2} opacity={0.85}>
            ({t('PIN, gate codes, etc.')})
          </Text>
        </Box>
      </Button>
    </Box>
  )
}
